import React, { useCallback, useEffect, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../components-ui/DropdownMenu';
import { ButtonStyled, NameStyled } from './PerformanceChartHeader/PerformanceChartHeader.styles';
import SvgSlidersHRegular from '../../../components/icons/SlidersHRegular';
import { SlidersHorizontal } from 'lucide-react';
import Checkbox from '../../../components/Checkbox';
import Typography from '../../../components/Typography';
import { useContentFilterStore } from '../../../store/content-filter';
import { useContentRulesStore } from '../../../store/content-rules';
import { useAttributionRulesStore } from '../../../store/attribution-rules';
import { useImpactGroupRulesStore } from '../../../store/impact-group-rules';
import { useLabels } from '../../../context/LabelsProvider';
import { useImpactGroups } from '../../../context/ImpactGroupsProvider';
import { NexoyaPortfolioLabel } from '../../../types';
import TextField from '../../../components/TextField';
import Radio from '../../../components/Radio';
import PortfolioFeatureSwitch from '../../../components/PortfolioFeatureSwitch';
import { PORTFOLIO_FEATURE_FLAGS } from '../../../constants/featureFlags';
import debounce from 'lodash/debounce';
import { usePortfolio } from '../../../context/PortfolioProvider';
import { usePortfolioParentContentFiltersQuery } from '../../../graphql/content/queryPortfolioParentContentFilters';
import { ChannelsFilterSection } from './ChannelsFilterSection';
import { SearchableFilterSubmenu } from './SearchableFilterSubmenu';
import { ContentLabelCategoriesFilterSection } from './ContentLabelCategoriesFilterSection';
import type { LabelCategory } from '../../settings/labels/types';

const NO_CATEGORIES: LabelCategory[] = [];

type Props = {
  /** The team's Content Labels v2 categories; while there are any, they replace the legacy Labels submenu. */
  contentLabelCategories?: LabelCategory[];
  /** False when the team hides the old portfolio labels (`useLabelsFlags().legacyEnabled`). */
  legacyLabelsEnabled?: boolean;
};

export const ContentFilter = ({ contentLabelCategories = NO_CATEGORIES, legacyLabelsEnabled = true }: Props) => {
  const { contentRules } = useContentRulesStore();
  const { attributionRules } = useAttributionRulesStore();
  const { impactGroupRules } = useImpactGroupRulesStore();
  const { labels } = useLabels();
  const { impactGroups } = useImpactGroups();
  const {
    portfolioV2Info: {
      meta: { data: portfolioV2 },
    },
  } = usePortfolio();
  const portfolioId = portfolioV2?.portfolioId;
  const isAttributed = portfolioV2?.isAttributed || false;

  const { data: parentContentFiltersData } = usePortfolioParentContentFiltersQuery({
    portfolioId,
    skip: !portfolioId,
  });

  const {
    labelsFilter,
    impactGroupsFilter,
    contentRulesFilter,
    attributionRulesFilter,
    impactGroupRulesFilter,

    isIncludedInOptimization,
    handleSetIsIncludedInOptimization,

    isRuleManaged,
    handleSetIsRuleManaged,

    titleContains,
    handleChangeTitleContains,

    handleAddLabel,
    handleRemoveLabel,
    includeNoLabel,
    handleToggleNoLabel,

    handleAddImpactGroup,
    handleRemoveImpactGroup,

    handleAddContentRule,
    handleRemoveContentRule,

    handleAddAttributionRule,
    handleRemoveAttributionRule,

    handleAddImpactGroupRule,
    handleRemoveImpactGroupRule,

    contentLabels,
    handleToggleContentLabelValue,
  } = useContentFilterStore();

  const hasContentLabelCategories = contentLabelCategories.length > 0;

  const [searchInputValue, setSearchInputValue] = useState(titleContains || '');

  useEffect(() => {
    debouncedSearch.cancel();
    setSearchInputValue(titleContains || '');
  }, [titleContains]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      handleChangeTitleContains(value);
    }, 350),
    [handleChangeTitleContains],
  );

  const availableProviders = parentContentFiltersData?.portfolioParentContentFilters?.providers;

  return (
    <div className="flex flex-col items-start">
      <div className="flex gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ButtonStyled variant="contained" color="secondary">
              <SvgSlidersHRegular />
              <span className="ml-1.5">Filter</span>
            </ButtonStyled>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60 font-normal" align="start">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                <span>Optimization status</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    className="w-52 justify-between"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSetIsIncludedInOptimization(true);
                    }}
                  >
                    Enabled
                    <Radio style={{ padding: 4 }} checked={isIncludedInOptimization === true} color="dark" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="w-52 justify-between"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSetIsIncludedInOptimization(false);
                    }}
                  >
                    Disabled
                    <Radio style={{ padding: 4 }} checked={isIncludedInOptimization === false} color="dark" />
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                <span>Content mode</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    className="w-52 justify-between"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSetIsRuleManaged(false);
                    }}
                  >
                    Manual
                    <Radio style={{ padding: 4 }} checked={isRuleManaged === false} color="dark" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="w-52 justify-between"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSetIsRuleManaged(true);
                    }}
                  >
                    Rule-based
                    <Radio style={{ padding: 4 }} checked={isRuleManaged === true} color="dark" />
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />

            {portfolioId && availableProviders?.length ? (
              <ChannelsFilterSection portfolioId={portfolioId} availableProviders={availableProviders} />
            ) : null}

            {/* v2 categories supersede the legacy labels here, as they do the legacy Label column. */}
            {hasContentLabelCategories || !legacyLabelsEnabled ? null : (
              <SearchableFilterSubmenu<NexoyaPortfolioLabel>
                triggerLabel="Labels"
                items={labels ?? []}
                getKey={(label) => label.labelId}
                getLabel={(label) => label?.name ?? ''}
                isActive={(label) => !!labelsFilter?.some((f) => f.labelId === label.labelId)}
                onToggle={(label) => {
                  const isActive = labelsFilter?.some((f) => f.labelId === label.labelId);
                  if (isActive) {
                    handleRemoveLabel(label);
                  } else {
                    handleAddLabel(label);
                  }
                }}
                searchPlaceholder="Search labels..."
                headerSlot={
                  <>
                    <DropdownMenuItem
                      className="flex w-full justify-between"
                      onClick={(e) => {
                        e.preventDefault();
                        handleToggleNoLabel();
                      }}
                    >
                      <NameStyled>
                        <Typography>No Label</Typography>
                      </NameStyled>
                      <Checkbox style={{ padding: 4 }} checked={includeNoLabel} color="dark" />
                    </DropdownMenuItem>
                    {labels?.length ? <DropdownMenuSeparator /> : null}
                  </>
                }
              />
            )}

            <SearchableFilterSubmenu
              triggerLabel="Impact groups"
              disabled={!impactGroups?.length}
              items={impactGroups ?? []}
              getKey={(g) => g.impactGroupId}
              getLabel={(g) => g?.name ?? ''}
              isActive={(g) => !!impactGroupsFilter?.some((f) => f.impactGroupId === g.impactGroupId)}
              onToggle={(g) => {
                const isActive = impactGroupsFilter?.some((f) => f.impactGroupId === g.impactGroupId);
                if (isActive) {
                  handleRemoveImpactGroup(g);
                } else {
                  handleAddImpactGroup(g);
                }
              }}
              searchPlaceholder="Search impact groups..."
            />

            <PortfolioFeatureSwitch
              features={[PORTFOLIO_FEATURE_FLAGS.SELF_SERVICE_PORTFOLIO]}
              renderOld={() => null}
              renderNew={() => (
                <>
                  <DropdownMenuSeparator />
                  <SearchableFilterSubmenu
                    triggerLabel="Content rules"
                    disabled={!contentRules?.length}
                    items={contentRules ?? []}
                    getKey={(r) => r.contentRuleId}
                    getLabel={(r) => r?.name ?? ''}
                    isActive={(r) => !!contentRulesFilter?.some((cr) => cr.contentRuleId === r.contentRuleId)}
                    onToggle={(r) => {
                      const isActive = contentRulesFilter?.some((cr) => cr.contentRuleId === r.contentRuleId);
                      if (isActive) {
                        handleRemoveContentRule(r);
                      } else {
                        handleAddContentRule(r);
                      }
                    }}
                    searchPlaceholder="Search content rules..."
                  />
                  <SearchableFilterSubmenu
                    triggerLabel="Impact group rules"
                    disabled={!impactGroupRules?.length}
                    items={impactGroupRules ?? []}
                    getKey={(r) => r.impactGroupRuleId}
                    getLabel={(r) => r?.name ?? ''}
                    isActive={(r) =>
                      !!impactGroupRulesFilter?.some((ir) => ir.impactGroupRuleId === r.impactGroupRuleId)
                    }
                    onToggle={(r) => {
                      const isActive = impactGroupRulesFilter?.some(
                        (ir) => ir.impactGroupRuleId === r.impactGroupRuleId,
                      );
                      if (isActive) {
                        handleRemoveImpactGroupRule(r);
                      } else {
                        handleAddImpactGroupRule(r);
                      }
                    }}
                    searchPlaceholder="Search impact group rules..."
                  />
                </>
              )}
            />

            {isAttributed ? (
              <SearchableFilterSubmenu
                triggerLabel="Attribution rules"
                disabled={!attributionRules?.length}
                items={attributionRules ?? []}
                getKey={(r) => r.attributionRuleId}
                getLabel={(r) => r?.name ?? ''}
                isActive={(r) => !!attributionRulesFilter?.some((cr) => cr.attributionRuleId === r.attributionRuleId)}
                onToggle={(r) => {
                  const isActive = attributionRulesFilter?.some((cr) => cr.attributionRuleId === r.attributionRuleId);
                  if (isActive) {
                    handleRemoveAttributionRule(r);
                  } else {
                    handleAddAttributionRule(r);
                  }
                }}
                searchPlaceholder="Search attribution rules..."
              />
            ) : null}

            <ContentLabelCategoriesFilterSection
              categories={contentLabelCategories}
              selected={contentLabels}
              onToggle={handleToggleContentLabelValue}
            />
          </DropdownMenuContent>
        </DropdownMenu>

        <TextField
          style={{ width: 304 }}
          wrapProps={{ style: { padding: '6px 12px' } }}
          placeholder="Search contents..."
          value={searchInputValue}
          name="titleContains"
          id="content-search"
          labelVariant="light"
          onChange={(e) => {
            const value = e?.target?.value;
            setSearchInputValue(value);
            debouncedSearch(value);
          }}
        />
      </div>
    </div>
  );
};
